import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { HSE_TERMS, type HseTermKey } from '../../constants/hseTerms';
import type { HseWeeklyDraft } from '../../types/hse-weekly-report.type';
import {
  hseCountColumnSx,
  hseCountInputSx,
  hseDescriptionColumnSx,
  hseHeadCountSx,
  hseHeadDescriptionSx,
  hseHeadNotesSx,
  hseHeadTermsSx,
  hseNotesColumnSx,
  hseNotesInputSx,
  hseTableContainerSx,
  hseTableSx,
  hseTermColumnSx,
  hseTermDefinitionSx,
  hseTermLabelSx,
} from './hseTable.styles';

export interface HseWeeklyReportTableProps {
  draft: HseWeeklyDraft;
  onRowChange: (termKey: HseTermKey, field: 'notes' | 'weeklyCount', value: string) => void;
  disabled?: boolean;
}

const HseWeeklyReportTable: React.FC<HseWeeklyReportTableProps> = ({
  draft,
  onRowChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow:
          theme.palette.mode === 'light'
            ? `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}, 0 8px 24px ${alpha(theme.palette.common.black, 0.05)}`
            : theme.shadows[4],
      }}
    >
      <TableContainer sx={hseTableContainerSx}>
        <Table stickyHeader size="small" aria-label={t('hse.tableAriaLabel')} sx={hseTableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={hseHeadTermsSx}>{t('hse.columns.terms')}</TableCell>
              <TableCell sx={hseHeadDescriptionSx}>{t('hse.columns.description')}</TableCell>
              <TableCell sx={hseHeadCountSx}>{t('hse.columns.weeklyCount')}</TableCell>
              <TableCell sx={hseHeadNotesSx}>{t('hse.columns.notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {HSE_TERMS.map((term) => {
              const termLabel = t(`hse.terms.${term.key}.label`);
              const termDefinition = t(`hse.terms.${term.key}.definition`);
              return (
                <TableRow key={term.key} hover>
                  <TableCell sx={hseTermColumnSx}>
                    <Typography component="span" sx={hseTermLabelSx}>
                      {termLabel}
                    </Typography>
                  </TableCell>
                  <TableCell sx={hseDescriptionColumnSx}>
                    <Tooltip title={termDefinition} placement="top-start" enterDelay={400}>
                      <Typography component="span" sx={hseTermDefinitionSx}>
                        {termDefinition}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={hseCountColumnSx}>
                    <TextField
                      type="number"
                      size="small"
                      variant="outlined"
                      value={draft[term.key].weeklyCount}
                      onChange={(e) => onRowChange(term.key, 'weeklyCount', e.target.value)}
                      disabled={disabled}
                      inputProps={{
                        min: 0,
                        step: 1,
                        'aria-label': t('hse.aria.weeklyCountFor', { term: termLabel }),
                      }}
                      sx={hseCountInputSx}
                    />
                  </TableCell>
                  <TableCell sx={hseNotesColumnSx}>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={draft[term.key].notes}
                      onChange={(e) => onRowChange(term.key, 'notes', e.target.value)}
                      disabled={disabled}
                      placeholder={t('hse.addNotesPlaceholder')}
                      multiline
                      minRows={1}
                      maxRows={3}
                      fullWidth
                      inputProps={{
                        maxLength: 2000,
                        'aria-label': t('hse.aria.notesFor', { term: termLabel }),
                      }}
                      sx={hseNotesInputSx}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default HseWeeklyReportTable;
